import classnames from 'classnames';
import { FormikState } from 'formik';
import { motion } from 'framer-motion';
import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useWizard } from 'react-use-wizard';

import * as orgRoutes from 'src/app/account/routes';
import { Form } from 'src/components/common/form/Form';
import { AnimatedWizardStep } from 'src/components/wizard/AnimatedWizardStep';
import { WizardHeader } from 'src/components/wizard/WizardHeader';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';

import { FlowTypeContext } from './FlowTypeProvider';
import {
  NewSupergraphOnboardingFields,
  getSupergraphOnboardingStepStrings,
} from './shared';

export const NewSupergraphOnboardingStepLayout = ({
  stepId,
  children,
  values,
  hasPrivateSubgraphs,
  ...animatedWizardStepProps
}: {
  stepId: string;
  children: React.ReactNode;
  values: FormikState<NewSupergraphOnboardingFields>['values'];
  hasPrivateSubgraphs: boolean;
} & React.ComponentProps<typeof AnimatedWizardStep>) => {
  const { flowType } = useContext(FlowTypeContext);
  const currentFlowSteps = getSupergraphOnboardingStepStrings(
    flowType,
    hasPrivateSubgraphs,
  );
  const { activeStep } = useWizard();
  const history = useHistory();
  const [currentAccountId] = useCurrentAccountId();
  const currentPlanResult = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const { isDedicatedPlan } = useCheckPlanTypes({
    tier: currentPlanResult?.currentPlan?.tier,
    kind: currentPlanResult?.currentPlan?.kind,
  });

  /**
   * Save the current values & step in url state whenever changes are made
   */
  useEffect(() => {
    if (!isDedicatedPlan) {
      history.replace(
        orgRoutes.supergraphOnboarding.locationFrom({
          location: history.location,
          fromRouteConfig: orgRoutes.supergraphOnboarding,
          patch: {
            formValues: {
              ...values,
              tab: values.tab as 'unregistered' | 'registered',
              stepIndex: activeStep,
            },
          },
        }),
      );
    }
  }, [activeStep, history, values, isDedicatedPlan]);

  return (
    <>
      <Form className={classnames('flex', 'flex-1')}>
        <div className="flex w-full flex-col items-center pb-24">
          <motion.div
            key={stepId}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className={classnames('mt-6', !isDedicatedPlan && 'w-120')}
          >
            <WizardHeader wizardSteps={currentFlowSteps} />
          </motion.div>

          <div className={classnames(!isDedicatedPlan && 'w-120')}>
            <AnimatedWizardStep
              className={classnames('size-full')}
              {...animatedWizardStepProps}
            >
              <motion.div layout="position" className="size-full pt-3">
                {children}
              </motion.div>
            </AnimatedWizardStep>
          </div>
        </div>
      </Form>
    </>
  );
};
